import { rgba } from 'polished';
import { ComponentProps, PropsWithChildren, ReactNode, useState } from 'react';
import * as dnd from 'react-beautiful-dnd';
import { Box, BoxProps, BoxType, Flex } from 'reflexbox';
import styled from 'styled-components';

import { tokens } from '../theme/tokens';
import { Spinner } from './Spinner';

export type ReorderRenderState = {
	dragHandleProps: dnd.DraggableProvided['dragHandleProps'];
	isDragging: boolean;
};

const Container = styled(Flex)<{ isHover?: boolean; isDragging: boolean }>`
	align-items: stretch;
	transition: 0.4s ease-out transform;

	border-radius: 10px;

	transition:
		0.4s ease-out background-color,
		0.4s ease-out box-shadow;

	${(props) =>
		props.isDragging
			? `
	background: ${tokens.border.subtle};
	box-shadow: 0px 16px 32px ${rgba(tokens.text.secondary, 0.25)};
	`
			: props.isHover &&
				`
	background: ${rgba(tokens.border.subtle, 0.5)};
	`}
`;

const Handle = styled(Flex)`
	flex-grow: 0;
	flex-shrink: 0;
	justify-content: center;
	flex-direction: column;
`;

const HandleBar = styled(Box)<{ isDragging: boolean }>`
	width: 16px;
	height: 2px;
	border-radius: 1px;
	background: ${(props) =>
		props.isDragging ? tokens.text.secondary : props.theme.colors.lightBlueDark};
	flex-shrink: 0;
	flex-grow: 0;

	transition: 0.4s ease-out background-color;
`;

const Contents = styled(Box)`
	flex-grow: 1;
	flex-shrink: 1;
`;

export function Reorder(
	props: PropsWithChildren<
		BoxProps & {
			id: string;
			index: number;
			loading: boolean;
			handle?: ReactNode;
			onHandleClick?: () => void;
			render?: (state: ReorderRenderState) => ReactNode;
		}
	>,
) {
	const { id, index, loading, onHandleClick, render, ...rest } = props;

	const [hover, setHover] = useState(false);

	return (
		<dnd.Draggable draggableId={id} index={index}>
			{(provided, snapshot) => (
				<Box
					{...(rest as ComponentProps<BoxType>)}
					ref={provided.innerRef}
					{...provided.draggableProps}
				>
					{render ? (
						render({
							dragHandleProps: provided.dragHandleProps,
							isDragging: snapshot.isDragging,
						})
					) : (
						<Container
							marginLeft={[-2, '-48px', '-48px']}
							alignItems="center"
							isDragging={snapshot.isDragging}
							isHover={hover}
						>
							{loading ? (
								<Handle px={3}>
									<Spinner />
								</Handle>
							) : (
								<Handle
									px={3}
									{...provided.dragHandleProps}
									onMouseEnter={() => setHover(true)}
									onMouseLeave={() => setHover(false)}
									onClick={() => onHandleClick?.()}
								>
									{props.handle ?? (
										<>
											<HandleBar mb={1} isDragging={hover || snapshot.isDragging} />
											<HandleBar mb={1} isDragging={hover || snapshot.isDragging} />
											<HandleBar isDragging={hover || snapshot.isDragging} />
										</>
									)}
								</Handle>
							)}
							<Contents>{props.children}</Contents>
						</Container>
					)}
				</Box>
			)}
		</dnd.Draggable>
	);
}

Reorder.Container = function ReorderContainer(
	props: PropsWithChildren<
		BoxProps & {
			onReorder: (id: string, index: number) => void;
		}
	>,
) {
	const { onReorder, ...rest } = props;

	return (
		<Box {...(rest as ComponentProps<BoxType>)}>
			<dnd.DragDropContext
				onDragEnd={(update) => {
					if (!update.destination) {
						return;
					}

					onReorder(update.draggableId, update.destination.index);
				}}
			>
				<dnd.Droppable droppableId="reorder">
					{(provided) => (
						<div ref={provided.innerRef} {...provided.droppableProps}>
							{props.children}
							{provided.placeholder}
						</div>
					)}
				</dnd.Droppable>
			</dnd.DragDropContext>
		</Box>
	);
};
